/**
 * Dispatching a settle, a snooze, or a visit from the browser.
 *
 * Each writes the new value into the store first and rolls back only if the
 * command fails. That is not a guess about the outcome: the row belongs to the
 * caller and the server never refuses one of these on its merits, so the
 * optimistic write is the same value a round trip earlier. What it buys is a
 * list that does not lurch — clicking "done" on a row and watching it sit there
 * for 80 ms before dropping is the thing this avoids.
 *
 * There is no sequence-based reconciliation here, unlike the upstream project
 * this is ported from. It needed one because its settled state lived on the
 * shared thread snapshot, so any refresh could overwrite an in-flight toggle
 * with a stale value. Here the per-person state has its own channel and is
 * carried across snapshot merges untouched, so the only thing that can
 * contradict an optimistic write is the event confirming it.
 */
import type { NativeApi, ThreadId } from "@taskin-code/contracts";

import { newCommandId } from "./utils";

type OrchestrationApi = NativeApi["orchestration"];

interface OptimisticStore {
  readonly applyOptimisticThreadUserState: (
    threadId: ThreadId,
    patch: { readonly settledAt?: string | null; readonly snoozedUntil?: string | null },
  ) => void;
}

export async function setThreadSettled(input: {
  readonly api: OrchestrationApi;
  readonly store: OptimisticStore;
  readonly threadId: ThreadId;
  readonly isSettled: boolean;
  /** What to restore if the command fails. */
  readonly previousSettledAt: string | null;
}): Promise<void> {
  const { api, store, threadId, isSettled, previousSettledAt } = input;
  store.applyOptimisticThreadUserState(threadId, {
    // A local instant, replaced by the server's own the moment the event lands.
    // Only its presence is read, never its value, so the brief disagreement
    // between the two clocks is not observable.
    settledAt: isSettled ? new Date().toISOString() : null,
  });
  try {
    await api.dispatchCommand({
      type: "thread.settle",
      commandId: newCommandId(),
      threadId,
      isSettled,
    });
  } catch (error) {
    store.applyOptimisticThreadUserState(threadId, { settledAt: previousSettledAt });
    throw error;
  }
}

export async function setThreadSnoozed(input: {
  readonly api: OrchestrationApi;
  readonly store: OptimisticStore;
  readonly threadId: ThreadId;
  /** Absolute instant, already resolved against the reader's clock. Null cancels. */
  readonly snoozeUntil: string | null;
  readonly previousSnoozedUntil: string | null;
}): Promise<void> {
  const { api, store, threadId, snoozeUntil, previousSnoozedUntil } = input;
  store.applyOptimisticThreadUserState(threadId, { snoozedUntil: snoozeUntil });
  try {
    await api.dispatchCommand({
      type: "thread.snooze",
      commandId: newCommandId(),
      threadId,
      snoozeUntil,
    });
  } catch (error) {
    store.applyOptimisticThreadUserState(threadId, { snoozedUntil: previousSnoozedUntil });
    throw error;
  }
}

/**
 * Tell the server this person looked at a thread.
 *
 * Fire-and-forget, and silent on failure by design: the store has already moved
 * on locally, and a toast saying "could not record that you read this" would be
 * noise about something nobody asked for. The value is re-sent on the next
 * visit anyway, and it only ever moves forward.
 */
export function recordThreadVisit(input: {
  readonly api: OrchestrationApi;
  readonly threadId: ThreadId;
  readonly visitedAt: string;
  /** Set when rewinding for mark-as-unread — the one write allowed to go back. */
  readonly allowRewind?: boolean;
}): void {
  void input.api
    .markThreadVisited({
      threadId: input.threadId,
      visitedAt: input.visitedAt,
      ...(input.allowRewind ? { allowRewind: true } : {}),
    })
    .catch(() => undefined);
}
